import { DownloadIcon } from "@/components/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { Presentation } from "@lala/harness/protocol";

import { errorMessage } from "../../lib/query.ts";

import { useDataset, type DatasetPayload } from "../dataset.ts";
import { EntityCard } from "./EntityCard.tsx";
import { downloadCsv, downloadXlsx } from "./export.ts";
import { formatDateOnly } from "./format.ts";
import { Kpi } from "./Kpi.tsx";
import { Ranking } from "./Ranking.tsx";
import { PresentationTable } from "./Table.tsx";
import { Timeseries } from "./Timeseries.tsx";

/**
 * La raíz de `present_result` (§4 de la auditoría del harness): un hallazgo
 * en una frase, una alerta opcional, el cuerpo según `intent`, "datos al",
 * las siguientes acciones y la descarga de las filas.
 *
 * Sin store, sin router, sin `useSearch`/`useNavigate`: este componente (y
 * los cinco de intención que delega) se van a extraer a un paquete
 * `@lala/harness-react` más adelante, así que todo lo que necesita del resto
 * de la aplicación entra por `onAsk`.
 */
export function PresentationPanel({
  presentation,
  onAsk,
}: {
  presentation: Presentation;
  onAsk: (question: string) => void;
}) {
  const state = useDataset(presentation.datasetId);

  return (
    <div className="grid gap-3">
      <p className="text-base font-medium">{presentation.headline}</p>

      {presentation.alert !== undefined && (
        <Alert
          variant={
            presentation.alert.severity === "info" ? "default" : "destructive"
          }
        >
          <AlertTitle>{severityLabel(presentation.alert.severity)}</AlertTitle>
          <AlertDescription>{presentation.alert.text}</AlertDescription>
        </Alert>
      )}

      {state.isPending && (
        <div
          className="h-24 animate-pulse rounded-lg border bg-card"
          aria-label="Cargando datos…"
        />
      )}

      {state.isError && (
        <Alert variant="destructive">
          <AlertTitle>No pude cargar los datos</AlertTitle>
          <AlertDescription>{errorMessage(state.error)}</AlertDescription>
        </Alert>
      )}

      {state.isSuccess && (
        <Body presentation={presentation} dataset={state.data} />
      )}

      <p className="text-xs text-muted-foreground">
        Datos al {formatDateOnly(presentation.dataAsOf)}
      </p>

      {presentation.nextActions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {presentation.nextActions.map((action, index) => (
            <button
              key={index}
              type="button"
              // El tooltip solo aporta algo cuando el botón no dice ya la
              // pregunta completa.
              title={
                action.question !== action.label ? action.question : undefined
              }
              className="rounded-full border px-3 py-1 text-xs hover:border-primary hover:text-primary"
              onClick={() => {
                onAsk(action.question);
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {state.isSuccess && (
        <div className="flex gap-3 text-xs text-muted-foreground">
          <button
            type="button"
            className="inline-flex items-center gap-1 hover:text-primary"
            onClick={() => {
              void downloadXlsx(state.data, `${presentation.datasetId}.xlsx`);
            }}
          >
            <DownloadIcon className="size-3.5" />
            Descargar Excel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 hover:text-primary"
            onClick={() => {
              downloadCsv(state.data, `${presentation.datasetId}.csv`);
            }}
          >
            <DownloadIcon className="size-3.5" />
            Descargar CSV
          </button>
        </div>
      )}
    </div>
  );
}

function Body({
  presentation,
  dataset,
}: {
  presentation: Presentation;
  dataset: DatasetPayload;
}) {
  switch (presentation.intent) {
    case "kpi":
      return <Kpi presentation={presentation} dataset={dataset} />;
    case "ranking":
      return <Ranking presentation={presentation} />;
    case "timeseries":
      return <Timeseries presentation={presentation} />;
    case "table":
      return <PresentationTable presentation={presentation} />;
    case "entity_card":
      return <EntityCard presentation={presentation} dataset={dataset} />;
  }
}

const severityLabel = (severity: "info" | "warning" | "critical"): string => {
  switch (severity) {
    case "info":
      return "Información";
    case "warning":
      return "Atención";
    case "critical":
      return "Crítico";
  }
};
